import {useState} from "react";
import { toast } from "sonner"
import {useNavigate, useParams} from "react-router-dom";
import { Check, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card.tsx";
import AuthService from "@/lib/auth";
import GuestLayout from "@/layouts/GuestLayout.tsx";

export default function GuestHIPPAConsent() {
  const navigate = useNavigate();
  const {patientId } = useParams();
  const [loading, setLoading] = useState(false);

  const handleHipaaConsent = async (accepted: boolean) => {
    setLoading(true);
    try {
      if (!patientId) {
        toast.error("No patient ID found!");
        return;
      }
      await AuthService.updatePatient(patientId, {
        hipaa_consent: accepted,
        hipaa_consent_date: new Date().toISOString(),
      });

      if (accepted) {
        navigate(`/visitor/id/${patientId}/questionnaire/phq-9`);
      } else {
        toast.error("HIPAA consent is required to proceed");
      }
    } catch (error) {
      toast.error("Error saving consent. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GuestLayout>
      <div className="w-full sm:px-6">
        <Card className="rounded-2xl shadow-lg shadow-green-200/40 border border-gray-200">
          <CardHeader className="border-b px-6 pt-6 pb-4">
            <div className="mb-4">
              <div className="flex items-center">
                   <FileText className="mr-3 text-green-600" size={24} />
                   <h2 className="text-xl sm:text-2xl font-bold text-gray-800">HIPAA Privacy Notice</h2>
              </div>

              <p>In case this form is being filled on behalf of patient , please ask the patients for consent</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div  style={{
              width: "100%", // Adjust dynamically if needed
              background: "linear-gradient(to right, #34d399, #facc15, #d1fae5)", // tailwind: from-green-400 via-yellow-400 to-green-100
            }} className="bg-blue-600 h-2 rounded-full w-2/4"></div>
            </div>
          </CardHeader>

          <CardContent className="p-6 max-h-100 overflow-y-auto text-sm text-gray-700 space-y-4">
            <p><strong>This notice describes how medical information about you may be used and disclosed and how you can get access to this information. Please review it carefully.</strong></p>

            <div>
              <h4 className="font-semibold mb-1">Uses and Disclosures of Protected Health Information</h4>
              <p>We may use and disclose your protected health information (PHI) for the following purposes:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li><strong>Treatment:</strong> Coordinating and managing your healthcare.</li>
                <li><strong>Payment:</strong> To receive payment for services.</li>
                <li><strong>Healthcare Operations:</strong> For internal healthcare management.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-1">Your Rights Regarding Your PHI</h4>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Request restrictions on disclosures</li>
                <li>Receive confidential communications</li>
                <li>Inspect and copy your PHI</li>
                <li>Request amendments</li>
                <li>Get an accounting of disclosures</li>
                <li>Request a paper copy of this notice</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-1">Complaints</h4>
              <p>If you believe your privacy rights have been violated, file a complaint with us or the Department of Health and Human Services.</p>
            </div>

            <p className="text-xs text-gray-500 mt-2">Effective as of January 1, 2024.</p>
          </CardContent>

          <CardFooter className="p-6 border-t flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => handleHipaaConsent(false)}
              disabled={loading}
              variant="destructive"
              className="w-full sm:w-1/2 text-white font-semibold h-[56px] text-base rounded-full bg-gradient-to-r from-red-400 to-red-500 hover:scale-[1.02] transition-transform shadow-lg shadow-green-300/40"
            >

               {loading ? (
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                    <X className="mr-2" size={16} />
                    Decline
                </>
              )}

            </Button>
            <Button
              onClick={() => handleHipaaConsent(true)}
              disabled={loading}
              className="w-full sm:w-1/2 text-white font-semibold h-[56px] text-base rounded-full bg-gradient-to-r from-green-400 to-green-500 hover:scale-[1.02] transition-transform shadow-lg shadow-green-300/40"
            >
              {loading ? (
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="mr-2" size={18} />
                  Accept & Continue
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </GuestLayout>
  );
}
